import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faSpinner,
  faExclamationCircle,
  faRedo,
  faReceipt,
  faEquals,
  faNotEqual,
  faBox,
  faTimes,
  faTag,
  faFileExport,
  faThumbsUp,
} from "@fortawesome/free-solid-svg-icons";
import apiService from "../../ApiService";
import { formatDateOnly } from "../../utils/DateUtils";
import "./BillProductSearch.css";

const SORT_OPTIONS = [
  { value: "name_asc", label: "Name A–Z" },
  { value: "name_desc", label: "Name Z–A" },
  { value: "occurrences_desc", label: "Most occurrences" },
  { value: "spread_desc", label: "Largest price spread" },
  { value: "min_price_asc", label: "Lowest price first" },
  { value: "min_price_desc", label: "Highest price first" },
];

const BillProductSearch = ({ onSelectBill }) => {
  const [productQuery, setProductQuery] = useState("");
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [sortBy, setSortBy] = useState("name_asc");
  const searchInputRef = useRef(null);

  const fetchBillsAndItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const userData = JSON.parse(localStorage.getItem("userData") || "{}");
      const res = await apiService.getBills({
        page: 1,
        per_page: 200,
        user_id: userData.UserID || null,
        sort_by: "bill_date",
        sort_order: "DESC",
      });
      const bills = res.bills || [];
      const items = [];
      for (const bill of bills) {
        try {
          const detail = await apiService.getBillById(bill.bill_id);
          const list = detail.items || [];
          list.forEach((it) => {
            items.push({
              bill_id: bill.bill_id,
              bill_date: detail.bill_date || bill.bill_date,
              merchant_name: detail.merchant_name || bill.merchant_name,
              item_name: it.item_name || "",
              unit_price: it.unit_price != null ? parseFloat(it.unit_price) : null,
              quantity: it.quantity,
              total_price: it.total_price,
              item_id: it.item_id,
            });
          });
        } catch (_) {}
      }
      setAllItems(items);
      setLoaded(true);
    } catch (err) {
      setError(err.message || "Failed to load bills and items");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBillsAndItems();
  }, [fetchBillsAndItems]);

  const normalizedQuery = (productQuery || "").trim().toLowerCase();
  const filtered = useMemo(
    () =>
      normalizedQuery
        ? allItems.filter((it) =>
            (it.item_name || "").toLowerCase().includes(normalizedQuery)
          )
        : [],
    [allItems, normalizedQuery]
  );

  const byProduct = useMemo(() => {
    const map = {};
    filtered.forEach((it) => {
      const key = (it.item_name || "").trim() || "(unnamed)";
      if (!map[key]) map[key] = [];
      map[key].push(it);
    });
    return map;
  }, [filtered]);

  const productGroupsWithStats = useMemo(() => {
    return Object.entries(byProduct).map(([productName, occurrences]) => {
      const prices = occurrences
        .map((o) => o.unit_price)
        .filter((p) => p != null && !isNaN(p));
      const minP = prices.length ? Math.min(...prices) : null;
      const maxP = prices.length ? Math.max(...prices) : null;
      const avgP =
        prices.length
          ? prices.reduce((a, b) => a + b, 0) / prices.length
          : null;
      const spread = minP != null && maxP != null ? maxP - minP : null;
      const samePrice = prices.length <= 1 || (minP !== null && maxP !== null && Math.abs(maxP - minP) < 0.005);
      const minPriceOcc = occurrences.find(
        (o) => o.unit_price != null && Math.abs(o.unit_price - minP) < 0.005
      );
      const maxPriceOcc = occurrences.find(
        (o) => o.unit_price != null && maxP != null && Math.abs(o.unit_price - maxP) < 0.005
      );
      return {
        productName,
        occurrences,
        minPrice: minP,
        maxPrice: maxP,
        avgPrice: avgP,
        spread,
        samePrice,
        minPriceOcc,
        maxPriceOcc,
      };
    });
  }, [byProduct]);

  const sortedGroups = useMemo(() => {
    const groups = [...productGroupsWithStats];
    switch (sortBy) {
      case "name_asc":
        return groups.sort((a, b) => a.productName.localeCompare(b.productName));
      case "name_desc":
        return groups.sort((a, b) => b.productName.localeCompare(a.productName));
      case "occurrences_desc":
        return groups.sort((a, b) => b.occurrences.length - a.occurrences.length);
      case "spread_desc":
        return groups.sort((a, b) => (b.spread ?? 0) - (a.spread ?? 0));
      case "min_price_asc":
        return groups.sort((a, b) => (a.minPrice ?? 0) - (b.minPrice ?? 0));
      case "min_price_desc":
        return groups.sort((a, b) => (b.minPrice ?? 0) - (a.minPrice ?? 0));
      default:
        return groups;
    }
  }, [productGroupsWithStats, sortBy]);

  const formatDate = (dateString) => formatDateOnly(dateString);

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return "—";
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  const handleExportCSV = () => {
    const rows = [
      ["Product", "Merchant", "Date", "Qty", "Unit Price", "Total", "Deal"],
    ];
    sortedGroups.forEach(({ productName, occurrences, minPriceOcc, maxPriceOcc }) => {
      occurrences.forEach((occ) => {
        const isBest = minPriceOcc && occ.bill_id === minPriceOcc.bill_id && occ.item_id === minPriceOcc.item_id;
        const isHigh = maxPriceOcc && occ.bill_id === maxPriceOcc.bill_id && occ.item_id === maxPriceOcc.item_id;
        let deal = "";
        if (isBest) deal = "Best deal";
        else if (isHigh && occurrences.length > 1) deal = "Highest";
        rows.push([
          productName,
          occ.merchant_name || "",
          formatDate(occ.bill_date),
          occ.quantity ?? "",
          occ.unit_price != null ? occ.unit_price.toFixed(2) : "",
          occ.total_price != null ? parseFloat(occ.total_price).toFixed(2) : "",
          deal,
        ]);
      });
    });
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `product-search-${normalizedQuery || "all"}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const totalOccurrences = filtered.length;
  const hasResults = sortedGroups.length > 0;

  if (loading && !loaded) {
    return (
      <div className="bill-product-search-loading">
        <FontAwesomeIcon icon={faSpinner} spin />
        <p>Loading bills and items...</p>
      </div>
    );
  }

  if (error && !loaded) {
    return (
      <div className="bill-product-search-error">
        <FontAwesomeIcon icon={faExclamationCircle} />
        <p>{error}</p>
        <button onClick={fetchBillsAndItems} className="retry-btn">
          <FontAwesomeIcon icon={faRedo} /> Retry
        </button>
      </div>
    );
  }

  return (
    <div className="bill-product-search-container">
      <div className="bill-product-search-header">
        <div>
          <h2>
            <FontAwesomeIcon icon={faBox} /> Product search
          </h2>
          <p className="bill-product-search-subtitle">
            Compare prices across bills and find the best deal for any product
          </p>
        </div>
        <button onClick={fetchBillsAndItems} className="refresh-btn" title="Reload data">
          <FontAwesomeIcon icon={faRedo} /> Refresh
        </button>
      </div>

      <div className="bill-product-search-toolbar">
        <div className="bill-product-search-input-wrap" ref={searchInputRef}>
          <FontAwesomeIcon icon={faSearch} />
          <input
            type="text"
            placeholder="Search by product name (e.g. milk, bread)..."
            value={productQuery}
            onChange={(e) => setProductQuery(e.target.value)}
            aria-label="Search products"
          />
          {productQuery && (
            <button
              type="button"
              className="bill-product-search-clear"
              onClick={() => setProductQuery("")}
              aria-label="Clear search"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          )}
        </div>
        {hasResults && (
          <>
            <div className="bill-product-search-sort">
              <label htmlFor="product-sort">Sort by</label>
              <select
                id="product-sort"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                {SORT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              className="bill-product-search-export"
              onClick={handleExportCSV}
            >
              <FontAwesomeIcon icon={faFileExport} /> Export CSV
            </button>
          </>
        )}
      </div>

      {!normalizedQuery ? (
        <div className="bill-product-search-empty">
          <FontAwesomeIcon icon={faSearch} />
          <p>Enter a product name to compare prices across your bills.</p>
          <p className="bill-product-search-hint">
            {allItems.length} line items loaded from your bills. Try &quot;milk&quot;, &quot;bread&quot;, or any item name.
          </p>
        </div>
      ) : !hasResults ? (
        <div className="bill-product-search-no-results">
          <FontAwesomeIcon icon={faReceipt} />
          <p>No products found for &quot;{productQuery}&quot;</p>
          <p className="bill-product-search-hint">Try a shorter or different term.</p>
        </div>
      ) : (
        <>
          <div className="bill-product-search-summary">
            <span className="bill-product-search-summary-item">
              <FontAwesomeIcon icon={faTag} />
              {sortedGroups.length} product{sortedGroups.length !== 1 ? "s" : ""}
            </span>
            <span className="bill-product-search-summary-item">
              <FontAwesomeIcon icon={faReceipt} />
              {totalOccurrences} occurrence{totalOccurrences !== 1 ? "s" : ""}
            </span>
          </div>

          <div className="bill-product-search-results">
            {sortedGroups.map(({ productName, occurrences, minPrice, maxPrice, avgPrice, spread, samePrice, minPriceOcc, maxPriceOcc }) => {
              return (
                <div key={productName} className="bill-product-group">
                  <div className="bill-product-group-header">
                    <div className="bill-product-group-title-row">
                      <span className="bill-product-name">{productName}</span>
                      <span
                        className={`bill-product-diff-badge ${samePrice ? "same" : "different"}`}
                        title={samePrice ? "Same unit price" : "Different prices"}
                      >
                        {samePrice ? (
                          <><FontAwesomeIcon icon={faEquals} /> Same price</>
                        ) : (
                          <><FontAwesomeIcon icon={faNotEqual} /> Price varies</>
                        )}
                      </span>
                    </div>
                    <div className="bill-product-stats">
                      <span title="Lowest unit price">Min {formatCurrency(minPrice)}</span>
                      <span className="bill-product-stats-sep">·</span>
                      <span title="Average unit price">Avg {formatCurrency(avgPrice)}</span>
                      <span className="bill-product-stats-sep">·</span>
                      <span title="Highest unit price">Max {formatCurrency(maxPrice)}</span>
                      {spread != null && spread > 0 && (
                        <>
                          <span className="bill-product-stats-sep">·</span>
                          <span title="Price spread">Spread {formatCurrency(spread)}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="bill-product-occurrences-wrap">
                    <div className="bill-product-occurrence-header">
                      <span>Merchant</span>
                      <span>Date</span>
                      <span>Qty</span>
                      <span>Unit price</span>
                      <span>Total</span>
                      <span className="occ-deal-col">Deal</span>
                    </div>
                    {occurrences.map((occ, idx) => {
                      const isBest = minPriceOcc && occ.bill_id === minPriceOcc.bill_id && occ.item_id === minPriceOcc.item_id;
                      const isHigh = maxPriceOcc && occ.bill_id === maxPriceOcc.bill_id && occ.item_id === maxPriceOcc.item_id && occurrences.length > 1;
                      return (
                        <div
                          key={`${occ.bill_id}-${occ.item_id}-${idx}`}
                          className="bill-product-occurrence"
                          onClick={() => onSelectBill && onSelectBill(occ.bill_id)}
                        >
                          <span className="occ-merchant">{occ.merchant_name}</span>
                          <span className="occ-date">{formatDate(occ.bill_date)}</span>
                          <span className="occ-qty">{occ.quantity}</span>
                          <span className="occ-price">{formatCurrency(occ.unit_price)}</span>
                          <span className="occ-total">{formatCurrency(occ.total_price)}</span>
                          <span className="occ-deal-col">
                            {isBest && (
                              <span className="occ-badge best-deal">
                                <FontAwesomeIcon icon={faThumbsUp} /> Best deal
                              </span>
                            )}
                            {isHigh && !isBest && (
                              <span className="occ-badge highest">Highest</span>
                            )}
                            {!isBest && !isHigh && <span className="occ-placeholder">—</span>}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default BillProductSearch;
